import { respondTo, StyledButton } from "@animech/ui";
import { css, CSSObject } from "@emotion/react";
import styled from "@emotion/styled";
import { leftPanelWidth } from "../layout-root-panel";
import { darkFloorForegroundColor } from "src/scene/room";

/* Keeps the top-right cluster clear of the bottombar toggle at top left. */
const bottombarToggleLane = "60px";

type Direction = "top" | "bottom" | "left" | "right";
type Alignment = Direction | "center";
type PositionProps = {
	position?: `${Alignment} ${Alignment}`;
};

export const MenuButton = styled.div``;
export const ProductInfo = styled.div``;
export const CartButtonAndInfoWrapper = styled.div``;
export const PriceContainer = styled.div``;
export const TotalPrice = styled.span``;
export const PriceDisclaimer = styled.span``;
export const PriceInfoBlock = styled.div``;

export const Position = styled.div<PositionProps>`
	${({ theme, position = "bottom right" }) => {
		const [vertical, horizontal] = position.toLowerCase().split(" ") as [
			Alignment,
			Alignment,
		];

		const getMobilePadding = () => {
			const styles: CSSObject = {};

			if (vertical !== "center") {
				const safeArea =
					vertical === "top"
						? "env(safe-area-inset-top, 0px)"
						: "env(safe-area-inset-bottom, 0px)";
				styles[vertical] =
					`calc(${theme.app.viewport.mobilePadding} + ${safeArea})`;
			}

			if (horizontal !== "center") {
				styles[horizontal] = theme.app.viewport.mobilePadding;
			}

			return styles;
		};

		let transform: string | undefined = undefined;
		const alignment: Record<Direction, string> = {
			top: "auto",
			bottom: "auto",
			left: "auto",
			right: "auto",
		};

		if (vertical === "center") {
			transform = "translateY(-50%)";
			alignment.top = "50%";
		} else {
			alignment[vertical] = theme.app.viewport.padding;
		}

		if (horizontal === "center") {
			alignment.left = "50%";
			transform = transform ? "translate(-50%, -50%)" : "translateX(-50%)";
		} else {
			alignment[horizontal] = theme.app.viewport.padding;
		}

		return css`
			display: flex;
			gap: ${theme.dimensions.margin.small};
			position: absolute;
			top: ${alignment.top};
			bottom: ${alignment.bottom};
			left: ${alignment.left};
			right: ${alignment.right};
			max-width: 100%;
			${transform && `transform: ${transform};`}
			line-height: ${theme.app.viewport.lineHeight};

			${respondTo(theme, "mobile")} {
				${getMobilePadding()}
				${position === "top right" &&
				css`
					flex-direction: column;
				`}
				${vertical === "bottom" &&
				css`
					transition: bottom ${theme.app.mobile.transitionDuration}ms
						${theme.app.mobile.transitionTimingFunction};

					[data-panel-open] > & {
						bottom: calc(
							${theme.app.viewport.mobilePadding} +
								env(safe-area-inset-bottom, 0px) +
								var(--mobile-panel-height, 0px)
						);
					}

					[data-panel-dragging] & {
						transition: none;
						bottom: calc(
							${theme.app.viewport.mobilePadding} +
								env(safe-area-inset-bottom, 0px) +
								var(--mobile-panel-visible-height, 0px)
						);
					}
				`}
			}
		`;
	}}
`;

export const ControlGroup = styled.span``;
export const Control = styled.span`
	flex: none;
`;
type ToolbarProps = {
	transparentButtons?: boolean;
};
export const Toolbar = styled.div<ToolbarProps>`
	${({ theme, transparentButtons }) => css`
		min-width: 0;

		${ControlGroup} {
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			gap: ${theme.dimensions.margin.small};
			line-height: ${theme.app.viewport.lineHeight};
			border-radius: ${theme.dimensions.borderRadius};
			background-color: ${theme.colors.backgrounds[0].background};
		}

		${Control} {
			display: flex;
			align-items: center;

			> label[data-shape="circle"] {
				border-radius: 50%;
			}

			button,
			label {
				aspect-ratio: 1;
			}

			> * {
				background-color: transparent !important;
				box-shadow: none !important;

				> * {
					background-color: transparent !important;
					box-shadow: none !important;
				}
			}
		}

		${respondTo(theme, "mobile")} {
			${ControlGroup} {
				background-color: transparent;
				justify-content: end;
				gap: ${transparentButtons ? 0 : theme.dimensions.margin.xSmall};

				${Control} {
					background-color: ${transparentButtons ? "transparent" : "white"};
					border-radius: 50%;

					button,
					label {
						height: 48px;
						width: 48px;
					}
				}

				${Control}[data-open="true"] {
					background-color: rgb(240, 240, 240);
				}
			}
		}
	`}
`;

export const DesignName = styled.p``;
export const DesigId = styled.p``;
export const DesignInfo = styled.div`
	${() => {
		return css`
			display: flex;
			position: absolute;
			gap: 4px;

			${DesignName} {
				font-weight: bold;
			}
			${DesigId} {
				user-select: text;
			}
		`;
	}}
`;

export const AppTitle = styled.span<{ overDarkFloor: boolean }>`
	${({ overDarkFloor }) =>
		overDarkFloor &&
		css`
			color: ${darkFloorForegroundColor};
		`}
`;

export const DynamicViewport = styled.div`
	position: relative;
	height: 100%;
	width: 100%;
`;

export const StaticViewport = styled.div<{
	leftSidebarOpen: boolean;
}>`
	${({ theme, leftSidebarOpen }) => {
		let widthOffset = "0px";
		if (leftSidebarOpen) {
			widthOffset = leftPanelWidth[0];
		}
		return css`
			position: absolute;
			top: 0;
			left: ${bottombarToggleLane};
			width: calc(100vw - ${widthOffset} - ${bottombarToggleLane});
			height: 100vh;
			height: 100dvh;

			transition: width ease-in-out 0.2s;

			${respondTo(theme, "mobile")} {
				width: 100vw;
			}

			${PriceContainer} {
				display: flex;
				flex-direction: column;
				pointer-events: none;
			}
		`;
	}}
`;

export const Root = styled.div`
	${({ theme }) => css`
		position: relative;
		height: 100%;
		width: 100%;
		grid-column: 1 / 1;
		grid-row: 1 / 1;
		pointer-events: none;

		${TotalPrice} {
			font-size: ${theme.typography.fontSize.large};
			text-align: right;
			line-height: 1;
		}

		${PriceDisclaimer} {
			font-size: ${theme.typography.fontSize.xSmall};
			text-align: right;
		}

		${CartButtonAndInfoWrapper} {
			display: flex;
			flex: none;
			min-width: var(--right-panel-offset);
			flex-direction: column;
			align-self: flex-end;
			font-family: ${theme.typography.fontFamily.secondary};
			font-weight: 300;
			gap: ${theme.dimensions.margin.base};

			${StyledButton.Root} {
				${StyledButton.Label} {
					text-transform: uppercase;
					font-size: ${theme.typography.fontSize.small};
				}
			}
		}

		${PriceInfoBlock} {
			position: absolute;
			top: calc(${theme.app.viewport.padding} + 43px);
			right: ${theme.app.viewport.padding};
			display: flex;
			flex-direction: column;
			align-items: flex-end;
			color: ${theme.colors.foregrounds[0]};
			font-family: ${theme.typography.fontFamily.secondary};
			font-weight: ${theme.typography.fontWeight.light};
			pointer-events: auto;
		}

		${DynamicViewport}, ${StaticViewport} {
			grid-column: 1 / 1;
			grid-row: 1 / 1;
			pointer-events: none;

			> * {
				pointer-events: auto;
			}

			${AppTitle} {
				text-transform: uppercase;
				line-height: 1;

				/* On mobile, we have less horizontal space, so we can afford to make the title wrap to multiple lines, by forcing a max-width. */
				${respondTo(theme, "mobile")} {
					max-width: 100px;
				}
			}

			${Position} {
				> button {
					font-weight: lighter;
					letter-spacing: 3px;
				}
			}
		}

		${respondTo(theme, "mobile")} {
			${TotalPrice} {
				display: flex;
				flex-direction: column;
				gap: 6px;
				font-size: 16px;
			}
		}
	`}
`;
